/* The person's menu. Tokens only (ADR-0007): a colour literal fails stylelint. */

/* The person is the last thing in the bar and is set apart from the context cluster by a hairline:
   the cluster says which figures these are, this says who is looking. Ghost until hovered, because
   an identity is not one of the bar's choices and four outlined controls at one weight was the
   problem. */
.wrap {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 34px;
  padding-left: 12px;
  border-left: 1px solid var(--line);
}

.trigger {
  display: flex;
  gap: 8px;
  align-items: center;
  height: 36px;
  padding: 0 8px 0 4px;
  font: inherit;
  color: var(--ink);
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-ctl);
  cursor: pointer;
  transition:
    background-color var(--dur-1) var(--ease-std),
    border-color var(--dur-1) var(--ease-std);
}

@media (width <= 560px) {
  /* 36px is a mouse's target; on a phone it is the control that holds Sign out (responsive pass,
     2026-10-30). */
  .trigger {
    height: 44px;
  }

  /* So the menu keeps its place on the bar's first row beside the bell. The role under the name stays
     whole: it answers "why is that button disabled?". */
  .name {
    max-width: 12ch;
  }
}

.trigger:hover {
  background: var(--panel-2);
  border-color: var(--line);
}

.trigger:active {
  background: var(--panel-3);
}

.trigger:disabled {
  color: var(--disabled);
  cursor: not-allowed;
}

.trigger[aria-expanded="true"] {
  background: var(--accent-w);
  border-color: var(--accent-line);
}

/* Initials on the accent tint. Text, not a drawn glyph (ADR-0089), and it hides below 1000px so the
   bar's overflow at 820px, which the browser gate measures on the bar itself, does not grow. */
.avatar {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 999px;
  background: var(--accent-w);
  box-shadow: inset 0 0 0 1px var(--accent-line);
  color: var(--accent);
  font-size: var(--t-min);
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
}

/* The initials repeat the name beside them, so they go as soon as the bar is short of room, not only
   at 1000px: at 1280px with the rail open the period read "Sierravale Foods Ltd · F" with the year
   cut off, and 34px of a decorative duplicate was the cheapest thing to give back (responsive pass,
   2026-10-30). */
@media (width <= 1280px) {
  .avatar {
    display: none;
  }
}

.who {
  display: grid;
  gap: 0;
  text-align: left;
  min-width: 0;
}

.name {
  /* 18ch: "Sample Contributor" was cut by 6px at 16ch, on every screen at every width. */
  max-width: 18ch;
  overflow: hidden;
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 16px;
  color: var(--ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The role, always visible - it answers "why is that button disabled?". The class name is read by
   the browser gate (`[class*="_role_"]`), so it is the one element carrying it. */
.role {
  display: inline-block;
  font-size: var(--t-min);
  font-weight: 500;
  line-height: 14px;
  color: var(--muted);
}

/* A chevron drawn from borders, so there is no glyph to depend on a font for. */
.caret {
  width: 6px;
  height: 6px;
  margin-top: -3px;
  border-right: 1.5px solid var(--muted);
  border-bottom: 1.5px solid var(--muted);
  transform: rotate(45deg);
  transition: transform var(--dur-1) var(--ease-std);
}

.trigger[aria-expanded="true"] .caret {
  margin-top: 3px;
  transform: rotate(225deg);
}

.menu {
  position: fixed;
  z-index: 30;
  display: grid;
  gap: 1px;
  min-width: min(210px, calc(100vw - 16px));
  max-width: calc(100vw - 16px);
  overflow-y: auto;
  padding: 4px;
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: var(--radius);
  box-shadow: var(--shadow-card);
}

.identity {
  display: grid;
  gap: 1px;
  padding: 6px 10px 8px;
  margin-bottom: 3px;
  border-bottom: 1px solid var(--line);
}

.identityName {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.identityKind {
  font-size: var(--t-min);
  color: var(--muted);
}

.item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 7px 10px;
  font: inherit;
  font-size: var(--t-dense);
  color: var(--ink);
  text-align: left;
  text-decoration: none;
  background: none;
  border: 0;
  border-radius: var(--radius-ctl);
  cursor: pointer;
  transition: background-color var(--dur-1) var(--ease-std);
}

.item:active {
  background: var(--accent-line);
}

.item:hover {
  background: var(--accent-w);
  color: var(--accent);
}

/* The menu is a roving-focus list, so the focused item must be as plain to see as the hovered one,
   and it carries the global accent ring inside its box. */
.item:focus-visible {
  background: var(--accent-w);
  color: var(--accent);
  outline: 2px solid var(--accent);
  outline-offset: -2px;
}

.item:disabled {
  color: var(--disabled);
  cursor: not-allowed;
}

.itemIcon {
  flex-shrink: 0;
  color: var(--muted);
}

.item:hover .itemIcon,
.item:focus-visible .itemIcon {
  color: var(--accent);
}

/* Sign out is set apart: it ends something, where the three above it go somewhere. */
.signOut {
  margin-top: 3px;
  border-top: 1px solid var(--line);
  border-radius: 0 0 var(--radius-ctl) var(--radius-ctl);
  padding-top: 8px;
}
